import Configurator from '@allesauto/core/configurator';
import CtaTeaser from '@allesauto/core/ctaTeaser';
import PDPFooterPrice from '@allesauto/core/footerPrice';
import JsonLd from '@allesauto/core/JsonLd';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import DummyCarMainDetails from '@allesauto/dummy/carMainDetails.json';
import {
  PaymentType,
  ProductType,
  QueryVehicleById,
  QueryVehiclePDP,
  SchemaTypes,
} from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import {
  consts,
  CtaTeaserTypes,
  FinanceTypeEn,
  FuelType,
  HeadlineVariation,
  Routes_DE,
  SupportedCurrencies,
  ToastifyType,
  TradeInFlowType,
} from '@allesauto/utils/consts';
import { VehicleCondition } from '@allesauto/utils/consts.ts';
import {
  createLinkForRouting,
  getHostFromWhitelabelConfig,
} from '@allesauto/utils/funcs';
import {
  formatPrice,
  getWhiteLabelCtaTeaserDescription,
} from '@allesauto/utils/funcs.ts';
import { Routes_DE as Routes } from '@allesauto/utils/routes';
import { useLazyQuery } from '@apollo/client';
import isNil from 'lodash/isNil';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Thing } from 'schema-dts';

import CarConfiguratorDealerOverlay from '@/components/CarConfigurator/CarConfiguratorDealerOverlay/CarConfiguratorDealerOverlay';
import Footer from '@/components/Footer/Footer';
import CarDealerDetails from '@/components/PDP/aboveFold/carDealerDetails/CarDealerDetails';
import CarDetailsFooter from '@/components/PDP/aboveFold/carDetailsFooter/CarDetailsFooter';
import CarGallery from '@/components/PDP/aboveFold/carGallery/CarGallery';
import CarMainDetails from '@/components/PDP/aboveFold/carMainDetails/CarMainDetails';
import CarSellerDetails from '@/components/PDP/aboveFold/carSellerDetails/CarSellerDetails';
import { IntersectionTest } from '@/components/PDP/aboveFold/intersectionTest';
import MainActions from '@/components/PDP/aboveFold/mainActions/MainActions';
import DealerInfo from '@/components/PDP/belowFold/dealerInfo/DealerInfo';
import EnvironmentEmission from '@/components/PDP/belowFold/environmentEmission/EnvironmentEmission';
import ExtrasCondition from '@/components/PDP/belowFold/ExtrasCondition/ExtrasCondition';
import Highlights from '@/components/PDP/belowFold/highlights/Highlights';
import ProductsCarousel from '@/components/ProductsCarousel/ProductsCarousel';
import AccessoriesTopRated from '@/components/ShoppingCart/Accessories/TopRated/AccessoriesTopRated';

import { getCarConfigMonthlyPrice } from '@/../../../packages/allesauto-core/src/lib/header/common/funcs';
import {
  useCarConfigurator,
  useCarConfiguratorQueryParams,
  useVehiclePDPData,
} from '@/features/PDP/hooks';
import { getWhiteLabelConfig } from '@/features/whitelabel/util/getWhiteLabelConfig';
import { initializeApollo } from '@/global/apollo/apollo';
import HeaderContainer from '@/global/containers/HeaderContainer';
import SeoContainer from '@/global/containers/SeoContainer';
import { featureflags } from '@/global/featureflags/featureflags';
import { useCustomer } from '@/global/hooks/useCustomer';
import { useAddToCartNotification } from '@/global/hooks/useNotificationAddToCart';
import useToken from '@/global/providers/auth';
import PDPLayout from '@/layouts/PDP';
import { closeOverlay, openOverlay } from '@/store/redux/actions/app';
import { getPDPState, setVehicle } from '@/store/redux/slices/pages/pdp';

export type PdpProps = {
  //
};

export type PdpStaticProps = {
  vehicle: SchemaTypes.Vehicle;
  host: string;
};

const Pdp: FC<React.PropsWithChildren<PdpProps & PdpStaticProps>> = ({
  vehicle,
  host,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const router = useRouter();
  const whiteLabel = useWhiteLabel();
  const reduxFilterPriceRange = useSelector(
    (state: any): any => (state.pages.pcp as any).filters.priceRange
  );
  const hasFavorites = featureflags.favoritesCompareEnabled;
  const didConfiguratorUpdate = useRef(false);

  const { carConfiguratorId, configuratorDealerId } =
    useCarConfiguratorQueryParams();

  const {
    data: carConfigurator,
    executeCall: executeConfiguratorVehicle,
    loading: isConfiguratorLoading,
  } = useCarConfigurator();

  const {
    id: vehicleId,
    makeName,
    makeNameCdSysteme,
    modelName,
    engineType,
    bodyColor,
    mileage,
    registeredIn,
    fuelType,
    powerHp,
    powerKw,
    images,
    mwst,
    vehicleCondition,
    vehicleDetail,
    price,
    cashOptions,
    similarVehicles,
    trim,
    isPrivateSeller,
    isFixedPrice,
    isOrderVehicle,
    bodyDesign,
    attributeGroups,
    configurationElements,
    emissionClassDetail,
    electricConsumption,
    consumption,
    title,
    deliveryDate,
    co2Class,
    co2ClassDischargedBatteryWltp,
  } = useVehiclePDPData(vehicle, carConfigurator);

  const { vehicle: vehicleState } = useSelector(getPDPState);

  const isConfigurableCar = useMemo(
    () =>
      vehicleCondition === consts.PDP.CONFIGURABLE_CAR_CONDITION ||
      vehicleCondition === consts.PDP.CONFIGURABLE_NEW_CAR,
    [vehicleCondition]
  );
  const hasConfiguratorDealer = useMemo(
    () => (!isConfigurableCar ? true : !!configuratorDealerId),
    [configuratorDealerId, isConfigurableCar]
  );

  const {
    dealer,
    highlights,
    comfort,
    entertainment,
    interior,
    security,
    vehicleHistory,
    environment,
    sellerLocation,
    sellerVehicleInfo,
  } = vehicleDetail ?? {};

  const { id: dealerId = '', name: dealerName } = dealer || {};
  const { customer } = useCustomer();
  const loggedIn = !!useToken();
  const { cartNotificationVehicle } = useAddToCartNotification();
  const headlineSimilarCars = 'ui.PDP.RECOMMENDED_CARS.HEADLINE';

  const [
    queryVehicleById,
    { loading: isFinanceLoading, data: dataVehicleFinance },
  ] = useLazyQuery(QueryVehicleById, {
    fetchPolicy: 'cache-and-network',
  });
  const financingDetails: SchemaTypes.FinancingDetail[] =
    dataVehicleFinance?.vehicleById?.financingDetails;
  const financeCalculatorOptions: SchemaTypes.FinanceCalculatorOptions =
    dataVehicleFinance?.vehicleById?.financeCalculatorOptions;

  const cheapestFinancing =
    (Array.isArray(financingDetails) &&
      financingDetails.length &&
      financingDetails.reduce((prev, curr) =>
        (prev?.monthlyRate || 0) < (curr?.monthlyRate || 0) ? prev : curr
      )) ||
    undefined;

  const paymentType: PaymentType =
    (vehicleState?.financing?.type as PaymentType) || PaymentType.CashRange;

  const carConfigMonthlyPrice = getCarConfigMonthlyPrice(
    carConfigurator?.financingDetails
  );
  const monthlyPrice =
    vehicleState?.financing?.monthlyInstallment ||
    (cheapestFinancing && cheapestFinancing?.monthlyRate);

  const handleConfiguratorError = useCallback(() => {
    import('@allesauto/core/toastify').then((module) => {
      module.toastifyFactory(
        ToastifyType.ERROR,
        translate(intl, 'global.ERRORS.DEFAULT.HEADER'),
        translate(intl, 'global.ERRORS.DEFAULT.TEXT')
      );
    });
    dispatch(closeOverlay());
  }, [dispatch, intl]);

  const openConfiguratorDealer = useCallback(() => {
    dispatch(
      openOverlay({
        children: (
          <CarConfiguratorDealerOverlay
            monthlyPrice={monthlyPrice ?? 0}
            make={makeName ?? ''}
            executeConfiguratorData={async (dealerId, configuratorId) =>
              await executeConfiguratorVehicle({
                vehicleId,
                configuratorId,
                dealerId,
              })
            }
            isConfigurableCar={isConfigurableCar}
          />
        ),
        title: translate(intl, 'ui.PDP.CAR_CONFIGURATOR.DEALER_OVERLAY_TITLE'),
        isLoading: isConfiguratorLoading,
      })
    );
  }, [
    dispatch,
    executeConfiguratorVehicle,
    intl,
    isConfigurableCar,
    isConfiguratorLoading,
    makeName,
    monthlyPrice,
    vehicleId,
  ]);

  const openConfigurator = useCallback(() => {
    pushAnalyticsEvent({
      event: 'configurator_start',
      ecommerce: {
        currency: SupportedCurrencies.EURO,
        value: price,
        items: [
          {
            item_name: `${makeName?.toLowerCase()} ${modelName?.toLowerCase()}`,
            item_id: vehicleId,
            price: price, //price of the car
            index: 0,
            quantity: 1,
          },
        ],
      },
    });
    dispatch(
      openOverlay({
        children: (
          <Configurator
            errorHandler={handleConfiguratorError}
            scriptURL={consts.API_URLS.EXTERNAL.CONFIGURATOR.SCRIPT_URL}
            stylesURL={consts.API_URLS.EXTERNAL.CONFIGURATOR.STYLES_URL}
            makeNameCdSysteme={makeNameCdSysteme || ''}
            modelName={modelName || ''}
            trim={trim || ''}
            bodyDesign={bodyDesign || ''}
            handleConfigurationFinished={() => {
              dispatch(closeOverlay());
              //Workaround for opening another overlay
              setTimeout(() => openConfiguratorDealer(), 1000);
            }}
          />
        ),
        withPaddings: false,
      })
    );
  }, [
    price,
    makeName,
    modelName,
    vehicleId,
    dispatch,
    handleConfiguratorError,
    makeNameCdSysteme,
    trim,
    bodyDesign,
    openConfiguratorDealer,
  ]);

  useEffect(
    () => {
      (async () =>
        await queryVehicleById({
          variables: {
            vehicleId,
          },
        }))();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicleId]
  );

  //For fist load when a query is ready and have parameters
  useEffect(
    () => {
      if (!carConfiguratorId || !configuratorDealerId) return;
      if (didConfiguratorUpdate.current) return;
      dispatch(
        setVehicle({
          price: carConfigurator?.price,
          persistentState: false,
        })
      );
      (async () =>
        await executeConfiguratorVehicle({
          vehicleId,
          configuratorId: carConfiguratorId,
          dealerId: configuratorDealerId,
        }))();
      didConfiguratorUpdate.current = true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.query]
  );

  useEffect(
    () => {
      const financingSelected: SchemaTypes.FinancingDetail | undefined =
        Array.isArray(financingDetails)
          ? (financingDetails.find(
              (fd) => fd?.type === reduxFilterPriceRange.key
            ) as SchemaTypes.FinancingDetail)
          : undefined;

      const persistentVehicleState = vehicleState.persistentState;

      if (!persistentVehicleState || vehicleState.vehicleId !== vehicleId) {
        dispatch(
          setVehicle({
            vehicleId,
            dealer,
            financing: {
              type: financingSelected?.type,
              downPayment: financingSelected?.prepayment,
              duration: financingSelected?.duration,
              mileage: financingSelected?.mileage,
              finalInstallment: financingSelected?.finalPayment,
              monthlyInstallment: financingSelected?.monthlyRate,
            },
            price,
            persistentState: false,
          })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, financingDetails?.length, vehicleId]
  );

  // Tag Manager on first load of the page
  useEffect(() => {
    //PDP Vehicle
    pushAnalyticsEvent({
      event: 'view_item',
      ecommerce: {
        currency: SupportedCurrencies.EURO,
        value: price,
        items: [
          {
            item_name: `${makeName?.toLowerCase()} ${modelName?.toLowerCase()}`,
            item_id: vehicleId,
            price: price,
            item_brand: makeName?.toLowerCase(),
            item_category: 'car',
            item_category2: vehicleCondition?.toLowerCase(),
            item_variant: bodyColor?.toLowerCase(),
            item_list_name: 'search results',
            item_list_id: 'search results',
            index: 0,
            quantity: 1,
          },
        ],
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  const [returnUrl, setReturnUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const returnUrl = searchParams.get('returnUrl');
    setReturnUrl(returnUrl ? decodeURIComponent(returnUrl) : undefined);
  }, []);

  const [calculateLoanSummary, setCalculateLoanSummary] = useState('');

  // Get similar vehicles
  const convertedSimilarVehicles = useMemo(
    () =>
      (similarVehicles &&
        similarVehicles.map((vehicle) => ({
          ...vehicle,
          title: '',
          makeName: vehicle.makeName || '',
          modelName: vehicle.modelName || '',
          subTitle: vehicle.vehicleCondition || '',
          path: `${(vehicle.images || [])[0]?.externalUrlLarge}`,
          productType: ProductType.Vehicle,
          price: vehicle.price || 0,
          bodyColor: vehicle.bodyColor || '',
        }))) ||
      [],
    [similarVehicles]
  );

  const headerPartial = (
    <HeaderContainer
      numOfNotificationsFavorite={consts.NAVIGATION_DUMMY_NOTIFICATIONS_NUMBER}
      cartNotificationVehicle={cartNotificationVehicle()}
    />
  );

  const [isFooterInteractive, setIsFooterInteractive] = useState(true);

  const today = new Date().toJSON().slice(0, 10);
  const imageBase =
    process.env.IMAGE_BASE_URL || process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '';

  const productJsonLd: Thing = {
    '@type': 'Product',
    name: title,
    image: images?.map((image) =>
      image?.externalUrlLarge ? `${imageBase}/${image.externalUrlLarge}` : ''
    ),
    description: trim || '',
    offers: {
      '@type': 'Offer',
      availability: 'InStock',
      priceCurrency: 'EUR',
      priceValidUntil: today,
      price: vehicle.price || 0,
      url:
        host +
        createLinkForRouting(FinanceTypeEn.EMPTY, Routes.PDP_VEHICLES, {
          markName: makeName && `/${makeName}`,
          modelName: modelName && `/${modelName}`,
          id: vehicleId && `/${vehicleId}`,
        }),
    },
    brand: {
      '@type': 'Brand',
      name: makeName || '',
    },
    sku: vehicleId,
    mpn: vehicleId,
  };

  const BOLD_START_INDEX = 28;
  const legalText: JSX.Element = (
    <p className={'text-grey80 text-p2'}>
      <b>
        {translate(intl, 'ui.PDP.VEHICLE_LEGAL_TEXT').substring(
          0,
          BOLD_START_INDEX
        )}
      </b>{' '}
      {translate(intl, 'ui.PDP.VEHICLE_LEGAL_TEXT').substring(BOLD_START_INDEX)}
    </p>
  );

  const priceLabelText = useMemo(
    () =>
      (isConfigurableCar &&
        !carConfiguratorId &&
        translate(intl, 'ui.PRICE.RECOMMENDED_PRICE_FROM')) ||
      undefined,
    [carConfiguratorId, intl, isConfigurableCar]
  );

  let downPaymentString;
  let durationString;
  let mileageString;
  let finalInstallmentString;

  const financingData = {
    downPayment: vehicleState?.financing?.downPayment,
    duration: vehicleState?.financing?.duration ?? 0,
    mileage: vehicleState?.financing?.mileage ?? 0,
    finalInstallment: vehicleState?.financing?.finalInstallment,
  };

  if (financingData && !isNil(financingData.downPayment))
    downPaymentString = formatPrice(intl, financingData.downPayment, {
      withFractionDigits: true,
    });

  if (financingData && !isNil(financingData.duration))
    durationString = intl.formatNumber(financingData.duration);

  if (financingData && !isNil(financingData.mileage))
    mileageString = intl.formatNumber(financingData.mileage);

  if (financingData && !isNil(financingData.finalInstallment))
    finalInstallmentString = formatPrice(intl, financingData.finalInstallment, {
      withFractionDigits: true,
    });

  return (
    <PDPLayout
      isLoading={
        (!router.isReady || isConfiguratorLoading) &&
        !didConfiguratorUpdate.current
      }
      headerContent={headerPartial}
      footerContent={<Footer />}
      loaderDescriptionText={translate(
        intl,
        'ui.PDP.CAR_CONFIGURATOR.LOADING_TEXT'
      )}
    >
      <JsonLd value={productJsonLd} />
      <SeoContainer
        pageHeadline={false}
        title={`${makeName || ''} ${modelName || ''}`}
      />
      <div
        data-bar="bar"
        className="bg-grey10 grid grid-cols-24 pt-15px w-full md:pt-30px lg:pb-100px lg:pt-[47px]"
      >
        <MainActions
          labelText={vehicleCondition || ''}
          linkText={translate(intl, 'ui.PDP.MAIN_ACTIONS.BACK_BUTTON_TEXT')}
          hasFavorites={hasFavorites}
          isWhiteLabel={whiteLabel !== undefined}
          returnUrl={returnUrl}
        />
        <CarMainDetails
          carManufacturer={makeName || ''}
          carModel={modelName || ''}
          engineType={engineType || ''}
          equipment={DummyCarMainDetails.equipment}
          mileage={mileage || 0}
          registrationDate={registeredIn || ''}
          fuelType={fuelType || ''}
          powerHp={powerHp || 0}
          powerKw={powerKw || 0}
          trim={trim || ''}
          bodyColor={bodyColor || ''}
        />
        <CarGallery
          vehicleImages={images as SchemaTypes.VehicleImage[]}
          isWhiteLabel={whiteLabel !== undefined}
        />
        {isPrivateSeller && sellerLocation ? (
          <CarSellerDetails location={sellerLocation} />
        ) : (
          <CarDealerDetails
            customerDetails={{
              ...customer,
            }}
            dealerDetails={{ ...dealer }}
            vehicleSku={vehicleDetail?.concreteProductIds[0] || ''}
            carManufacturer={makeName || ''}
            carModel={modelName || ''}
            vehicleId={vehicleId}
            isOrderVehicle={isOrderVehicle}
            carConfigurator={{
              isConfigurableCar: isConfigurableCar,
              handleConfiguratorDealer: openConfiguratorDealer,
              hasCarConfiguratorDealer: hasConfiguratorDealer,
              carConfiguratorId: carConfiguratorId,
            }}
            vehiclePrice={vehicleState.price ?? price ?? undefined}
            paymentType={paymentType}
            openConfigurator={openConfigurator}
            financeCalculationResult={
              paymentType === PaymentType.CashRange
                ? undefined
                : {
                    downPayment: vehicleState?.financing?.downPayment,
                    duration: vehicleState?.financing?.duration ?? 0,
                    mileage: vehicleState?.financing?.mileage ?? 0,
                    monthlyInstallment:
                      vehicleState?.financing?.monthlyInstallment ?? 0,
                  }
            }
          />
        )}
      </div>

      <IntersectionTest onChange={setIsFooterInteractive} />
      <CarDetailsFooter
        vehicleId={vehicleId}
        carManufacturer={makeName || ''}
        carModel={modelName || ''}
        payment={paymentType}
        price={vehicleState.price ?? price ?? 0}
        monthlyPrice={
          carConfigMonthlyPrice ? carConfigMonthlyPrice : monthlyPrice
        }
        cashOptions={cashOptions}
        financing={financingData}
        financingDetails={financingDetails as SchemaTypes.FinancingDetail[]}
        financeCalculatorOptions={financeCalculatorOptions}
        isPrivateSeller={isPrivateSeller}
        isOrderVehicle={isOrderVehicle}
        isFinanceLoading={
          isFinanceLoading && !isConfiguratorLoading && router.isReady
        }
        isFixedPrice={isFixedPrice}
        isInteractive={isFooterInteractive}
        dealerId={dealerId}
        dealerName={dealerName ?? ''}
        sku={vehicleDetail?.concreteProductIds[0] || ''}
        contactPerson={{
          firstName: dealer?.contactPerson?.firstName || '',
          lastName: dealer?.contactPerson?.lastName || '',
          phone: dealer?.contactPerson?.phone || '',
        }}
        vehicleCondition={vehicleCondition}
        bodyColor={bodyColor}
        customerDetails={{
          ...customer,
        }}
        isConfigurableCar={isConfigurableCar}
        carConfiguratorId={carConfiguratorId}
        openConfigurator={openConfigurator}
        configuratorDealerId={configuratorDealerId}
        openConfiguratorDealerSelection={openConfiguratorDealer}
        // data for ContactDealerForm
        dealerDetails={{ ...dealer }}
        vehicleSku={vehicleDetail?.concreteProductIds[0] || ''}
        vehiclePrice={vehicleState.price ?? price ?? undefined}
        priceLabelText={priceLabelText}
        downPaymentString={downPaymentString}
        durationString={durationString}
        mileageString={mileageString}
        finalInstallmentString={finalInstallmentString}
        setCalculateLoanSummary={setCalculateLoanSummary}
      />

      <AccessoriesTopRated vehiclePdpItem={vehicle} />

      <div className="bg-white grid grid-cols-24 w-full">
        <div
          className={`col-end-2 col-start-20 content-end flex justify-between ${
            whiteLabel?.merchantOrigin === 'schneidergruppe-de' ? 'mb-35px' : ''
          }`}
        >
          {mwst && vehicleCondition === VehicleCondition.USED && (
            <div className="pr-[16px] pt-[55px]">{legalText}</div>
          )}
        </div>
        <div
          className="col-end-25 col-start-20 content-end flex justify-end pr-[16px] pt-[55px] text-grey80 text-p2"
          data-style-for-print="price-productId-container"
        >
          <div>
            {translate(intl, 'ui.PDP.PRODUCT_NUMBER.TITLE', {
              productNumber: vehicleDetail?.internalId ?? '',
            })}
          </div>
          <div className="hidden" data-style-for-print="pdp-price">
            <PDPFooterPrice
              paymentType={paymentType}
              price={price}
              monthlyInstallment={monthlyPrice}
              isPrivateSeller={isPrivateSeller}
              isFixedPrice={isFixedPrice}
              priceLabel={priceLabelText}
            />
          </div>
          <div className="hidden" data-style-for-print="pdp-payment-type-info">
            {!isPrivateSeller &&
              (paymentType === PaymentType.LeasingRateRange ||
                paymentType === PaymentType.LoanRateRange) && (
                <>
                  <div className={'text-p3 monochrome:text-white'}>
                    {downPaymentString && (
                      <span>
                        <span id="label--down-payment-value">
                          {downPaymentString}
                        </span>
                        <span id="label--down-payment">{`${translate(
                          intl,
                          'ui.PDP.FOOTER.DEPOSIT'
                        )}`}</span>
                      </span>
                    )}
                    {durationString && (
                      <span className="pl-10px">
                        <span id="label--duration-value">{durationString}</span>
                        <span id="label--duration">
                          {translate(intl, 'ui.PDP.FOOTER.MONTHS')}
                        </span>
                      </span>
                    )}
                    {mileageString && (
                      <span className="pl-10px">
                        <span id="label--mileage-value">{mileageString}</span>
                        <span id="label--mileage">
                          {translate(intl, 'ui.PDP.FOOTER.MILEAGE')}
                        </span>
                      </span>
                    )}
                    {paymentType === PaymentType.LoanRateRange &&
                      finalInstallmentString && (
                        <span className="pl-10px">
                          <span id="label--price">
                            {finalInstallmentString}
                          </span>
                          <span id="label--payment-option">
                            {translate(intl, 'ui.PDP.FOOTER.FINAL_INSTALLMENT')}
                          </span>
                        </span>
                      )}
                  </div>
                </>
              )}

            {paymentType === PaymentType.LoanRateRange &&
              financeCalculatorOptions &&
              financeCalculatorOptions.loan && (
                <>
                  <div className="mt-[24px]">
                    <span className="pl-10px">
                      <span id="label--payment-option">
                        {calculateLoanSummary ||
                          financingDetails[0]?.summary ||
                          ''}
                      </span>
                    </span>
                  </div>
                  <div className="mt-[24px]">
                    <span className="pl-10px">
                      <span id="label--payment-option">
                        {financeCalculatorOptions.loan.legalText}
                      </span>
                    </span>
                  </div>
                </>
              )}
            {paymentType === PaymentType.LeasingRateRange && (
              <div className="mt-[24px]">
                {financeCalculatorOptions &&
                  financeCalculatorOptions.leasing && (
                    <span className="pl-10px">
                      <span id="label--payment-option">
                        {financeCalculatorOptions.leasing.legalText}
                      </span>
                    </span>
                  )}
              </div>
            )}
          </div>
        </div>

        {whiteLabel?.merchantOrigin != 'schneidergruppe-de' && (
          <Highlights
            highlights={highlights}
            configurationElements={configurationElements}
          />
        )}

        <ExtrasCondition
          comfort={comfort}
          entertainment={entertainment}
          interior={interior}
          exterior={{ bodyColor: bodyColor ?? '' }}
          security={security}
          vehicleHistory={vehicleHistory}
          sellerVehicleInfo={isPrivateSeller ? sellerVehicleInfo : ''}
          attributeGroups={attributeGroups}
          deliveryDate={deliveryDate}
          guaranteeUrl={dealer?.guaranteeUrl}
        />

        <EnvironmentEmission
          electricConsumption={electricConsumption}
          consumption={consumption}
          co2Class={co2Class}
          co2ClassDischargedBatteryWltp={co2ClassDischargedBatteryWltp}
          environment={environment}
          fuelType={fuelType as FuelType}
          isPrivateSeller={isPrivateSeller}
          emissionClassDetail={emissionClassDetail}
          hasConfiguratorDealer={!!configuratorDealerId}
        />

        {!isOrderVehicle && !isConfigurableCar && (
          <CtaTeaser
            teaserType={CtaTeaserTypes.FRAME}
            headline={translate(intl, 'ui.TRADE_IN.PDP.HEADLINE')}
            description={translate(
              intl,
              whiteLabel
                ? getWhiteLabelCtaTeaserDescription(whiteLabel)
                : 'ui.TRADE_IN.PDP.DESCRIPTION'
            )}
            ctaLabel={translate(intl, 'ui.TRADE_IN.PDP.BUTTON_TEXT')}
            className="col-end-25 col-start-1"
            bgClassName="bg-grey10"
            textClassName="text-grey100"
            ctaAction={() => {
              router.push(
                loggedIn
                  ? `${Routes_DE.TRADE_IN}?${TradeInFlowType.PDP}=${encodeURI(
                      `${makeName}/${modelName}/${vehicleId}`
                    )}`
                  : `${Routes_DE.ACCOUNT.LOGIN}?redirectTo=${encodeURIComponent(
                      `${Routes_DE.TRADE_IN}?${TradeInFlowType.PDP}=${makeName}/${modelName}/${vehicleId}`
                    )}`
              );
            }}
            idButton="button--tradeIn"
            component={''}
            _uid={''}
          />
        )}

        {!isPrivateSeller && hasConfiguratorDealer && (
          <DealerInfo
            dealerDetails={dealer || { id: '' }}
            openingHours={dealer?.retailOpeningHours || []}
            carMake={makeName || ''}
            carModel={modelName || ''}
            vehicleId={vehicleDetail?.concreteProductIds[0] || ''}
            customerDetails={{
              ...customer,
            }}
            paymentType={paymentType}
            financeCalculationResult={
              paymentType === PaymentType.CashRange
                ? undefined
                : {
                    downPayment: vehicleState?.financing?.downPayment,
                    duration: vehicleState?.financing?.duration ?? 0,
                    mileage: vehicleState?.financing?.mileage ?? 0,
                    monthlyInstallment:
                      vehicleState?.financing?.monthlyInstallment ?? 0,
                  }
            }
            vehiclePrice={vehicleState.price ?? price ?? undefined}
          />
        )}

        {convertedSimilarVehicles &&
          !!convertedSimilarVehicles.length &&
          whiteLabel?.merchantOrigin != 'schneidergruppe-de' && (
            <ProductsCarousel
              products={convertedSimilarVehicles}
              headlineKeyTranslation={headlineSimilarCars}
              typeTestId="similar-vehicles"
              headlineType={HeadlineVariation.H2}
            />
          )}
      </div>
    </PDPLayout>
  );
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps<PdpStaticProps> = async (
  context
) => {
  const { dealer, id } = context.params as Record<string, string>;
  const whitelabel = await getWhiteLabelConfig(dealer);
  const apolloClient = initializeApollo(
    null,
    whitelabel?.merchantUuids,
    whitelabel?.merchantOrigin
  );
  const { data } = await apolloClient.query<
    Pick<SchemaTypes.Query, 'vehicleById'>
  >({
    query: QueryVehiclePDP,
    variables: {
      vehicleId: id,
    },
    errorPolicy: 'all',
  });

  const vehicle = data?.vehicleById;
  const host = getHostFromWhitelabelConfig(whitelabel) || 'https://alles.auto';

  if (!vehicle) {
    return {
      notFound: true,
      revalidate: 60, // revalidate after 1 minute
    };
  }

  return {
    props: {
      vehicle,
      host,
    },
    revalidate: 60 * 60, // cache for 1 hour
  };
};

export default Pdp;
